import { Link, StaticQuery, graphql } from "gatsby";
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import Icon from "./icon";
import { cn } from "../lib/helpers";
import { seoUrl } from "../lib/helpers";
import Container from "./container";
import { useCookies } from 'react-cookie';
import { imageUrlFor } from "../lib/image-url";
import { buildImageObj } from "../lib/helpers";
import PortableText from "./portableText";
import Links from "./links-reverse";

import * as styles from "../styles/components/header.module.css";

const query = graphql`
  query HeaderQuery {
    site: sanitySiteSettings(_id: { regex: "/(drafts.|)siteSettings/" }) {
      notificationBar {
        heading
        _rawContent
      }
      headerUtilities {
        buttons {
          blank
          secondaryButton
          text
          url
        }
      }
    }
    mainnav: allSanityNav(filter: { title: { eq: "Main Navigation" } }) {
      edges {
        node {
          navItems {
            _key
            blank
            url
            text
            subNav {
              _key
              blank
              description
              text
              url
              sectionHeader
              icon {
                asset {
                  _id
                }
              }
            }
          }
        }
      }
    }
  }
`;

const Header = (props) => {

  const {
    dark
  } = props;

  const [showNav, setShowNav] = useState(false);
  const [hideNfBar, setHideNfBar] = useState(true);
  const [cookies, setCookie] = useCookies(['hideNotificationBar']);

  // Header element/height
  const headerRef = useRef(null);
  const navRef = useRef(null);
  let headerHeight;

  function handleShowNav() {
    setShowNav(true);
    headerHeight = headerRef.current.clientHeight;
    navRef.current.style.height = 'calc(100% - ' + headerHeight + 'px)';
  }

  function handleHideNav() {
    const mobile = window.matchMedia(`(max-width: 1200px)`).matches;

    if(mobile) {
      setShowNav(false);
      navRef.current.style.height = 0;
      const parents = document.getElementsByClassName(styles.hasChildren);
      for (const parent of parents) {
        parent.classList.remove(styles.active);
      }
    }
  }

  function handleToggleSubNav(e) {
    e.preventDefault();
    e.target.parentElement.classList.toggle(styles.active);
  }

  function handleHideNotificationBar() {
    setCookie('hideNotificationBar', true, {maxAge: 604800});
    setHideNfBar(true);
  }

  function handleSetHideNfBar() {
    if(cookies.hideNotificationBar === undefined) {
      setHideNfBar(false);
    }
  }

  function updateHeight() {
    const mobile = window.matchMedia(`(max-width: 1200px)`).matches;
    headerHeight = headerRef.current.clientHeight;

    if(mobile) {
      navRef.current.style.top = headerHeight + 'px';
      if(showNav) {
        navRef.current.style.height = 'calc(100% - ' + headerHeight + 'px)';
      }
    }
    else {
      navRef.current.style.top = '';
      navRef.current.style.height = '';
    }
  }

  useEffect(() => {
    handleSetHideNfBar();
  });

  // update height on resize
  useLayoutEffect(() => {

    let checkExist = setInterval(function() {
      if (headerRef.current) {
        updateHeight();
        clearInterval(checkExist);
      }
    }, 100); // check every 100ms

    window.addEventListener('resize', updateHeight);
    
    return () => window.removeEventListener('resize', updateHeight);
  });

  return (
  <StaticQuery
    query={query}
    render={data => (
      <header className={cn(styles.root, dark && styles.dark)} ref={headerRef}>
        <Container>

          <div className={styles.inner}>

          <div className={styles.main}>
            <div className="grid align-start justify-between">
              <div className={cn("col-auto", styles.mainCol)}>

                <div className={styles.branding}>
                  <Link to="/"><Icon symbol="logo" /></Link>
                </div>

                <div className={cn(styles.navWrap, showNav && styles.active)} ref={navRef}>

                  <nav className={styles.mainNavBlock}>
                    {data && data.mainnav.edges.map(({node: mainnav}) => (
                      <>
                        {mainnav.navItems && mainnav.navItems.length && 
                          <ul className={styles.mainNav}>
                            {mainnav.navItems.map(navItems => (
                              <>
                              {(navItems.subNav && navItems.subNav.length)
                                ? <li className={styles.hasChildren} key={navItems._key}>
                                    {navItems.blank
                                      ? <a href={navItems.url + '/'} target="_blank" rel="noreferrer" onClick={handleToggleSubNav}>{navItems.text}<div className={styles.mHide}><Icon symbol="chevron-down" /></div><div className={styles.dHide}><Icon symbol="plus" /><Icon symbol="minus" /></div></a>
                                      : <Link to={navItems.url + '/'} onClick={handleToggleSubNav}>{navItems.text}<div className={styles.mHide}><Icon symbol="chevron-down" /></div><div className={styles.dHide}><Icon symbol="plus" /><Icon symbol="minus" /></div></Link>
                                    }
                                    <ul className={cn(styles.subMenu, seoUrl(navItems.text))}>
                                      {navItems.subNav.map(sn => {
                                        const r = {
                                          ...sn
                                        }
                                        // hack because industries/supply-chain-teams was returning without the leading slash
                                        // and I can't figure out why
                                        if (r.url && !r.url.startsWith("/") && !r.url.startsWith("http")) {
                                          r.url = "/" + r.url
                                        }
                                        if (r.url && r.url.endsWith('/')) {
                                          r.url = r.url.slice(0, -1)
                                        }
                                        return r
                                      }).map(subNav => (
                                        <li key={subNav._key}>
                                          {subNav.sectionHeader && <div className={styles.sectionHeader}>{subNav.sectionHeader}</div>}
                                          {subNav.blank
                                            ? <a href={subNav.url} target="_blank" rel="noreferrer" onClick={handleHideNav}>
                                                {subNav.icon && subNav.icon.asset && 
                                                  <div className={styles.icon}>
                                                    <img
                                                      src={imageUrlFor(buildImageObj(subNav.icon))
                                                        .auto("format")
                                                        .url()}
                                                      alt=""
                                                    />
                                                  </div>
                                                }
                                                <div>{subNav.text}</div>
                                                <div className={styles.description}>{subNav.description}</div>
                                              </a>
                                            : <Link to={subNav.url + '/'} onClick={handleHideNav}>
                                                {subNav.icon && subNav.icon.asset && 
                                                  <div className={styles.icon}>
                                                    <img
                                                      src={imageUrlFor(buildImageObj(subNav.icon))
                                                        .auto("format")
                                                        .url()}
                                                      alt=""
                                                    />
                                                  </div>
                                                }
                                                <div>{subNav.text}</div>
                                                <div className={styles.description}>{subNav.description}</div>
                                              </Link>
                                          }
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                : <li key={navItems._key}>
                                    {navItems.blank
                                      ? <a href={navItems.url + '/'} target="_blank" rel="noreferrer" onClick={handleHideNav}>{navItems.text}</a>
                                      : <Link to={navItems.url + '/'} onClick={handleHideNav}>{navItems.text}</Link>
                                    }
                                  </li>
                              }
                              </>
                            ))}
                          </ul>
                        }
                      </>
                    ))}
                    {data.site.headerUtilities.buttons.length &&
                      <div className={cn(styles.utilities, styles.dHide)}>
                        <Links buttons={data.site.headerUtilities.buttons} dark={dark} />
                      </div>
                    }
                  </nav>

                </div>
              </div>
              <div className="col-auto">

              {data.site.headerUtilities.buttons.length &&
                      <div className={cn(styles.utilities, styles.mHide)}>
                        <Links buttons={data.site.headerUtilities.buttons} dark={dark} />
                      </div>
              }

                <button
                  className={cn(styles.menuToggle, showNav && styles.active, styles.dHide)}
                  onClick={showNav ? handleHideNav : handleShowNav}
                >
                  <span className={styles.icon}></span>
                  <span className="screen-reader-text">Menu</span>
                </button>

              </div>
            </div>
          </div>

          {data.site.notificationBar && data.site.notificationBar._rawContent && !hideNfBar && 
            <div className={styles.notificationBar}>
              {data.site.notificationBar.heading && <div className={styles.heading}>{data.site.notificationBar.heading}</div>}
              <PortableText blocks={data.site.notificationBar._rawContent} />
              <button onClick={handleHideNotificationBar}>
                <span className="screen-reader-text">Close</span>
                <Icon symbol="close" />
              </button>
            </div>
          }

          </div>

        </Container>

      </header>
    )}
  />
)};

export default Header;
